import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";

const copy = {
  id: {
    title: "Pengguna Mindovia",
    subtitle: "Lihat akun pengguna yang terdaftar tanpa membuka data asesmen pribadi mereka.",
    search: "Cari email...", status: "Status Akun", verification: "Verifikasi Email",
    sort: "Urutkan", all: "Semua", active: "Aktif", deleting: "Dalam proses penghapusan",
    verified: "Terverifikasi", unverified: "Belum terverifikasi", saved: "Hasil Tersimpan",
    joined: "Tanggal Daftar", newest: "Terbaru", oldest: "Terlama", emailAsc: "Email A–Z",
    emailDesc: "Email Z–A", empty: "Belum ada pengguna yang sesuai.",
    error: "Data pengguna tidak dapat dimuat. Coba lagi.", previous: "Sebelumnya",
    next: "Berikutnya", page: "Halaman",
  },
  en: {
    title: "Mindovia Users",
    subtitle: "View registered user accounts without exposing their private assessment data.",
    search: "Search email...", status: "Account Status", verification: "Email Verification",
    sort: "Sort", all: "All", active: "Active", deleting: "Deleting", verified: "Verified",
    unverified: "Not verified", saved: "Saved Results", joined: "Joined", newest: "Newest",
    oldest: "Oldest", emailAsc: "Email A–Z", emailDesc: "Email Z–A",
    empty: "No matching users found.", error: "Users could not be loaded. Please try again.",
    previous: "Previous", next: "Next", page: "Page",
  },
};

function joinedDate(value, lang) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(lang === "id" ? "id-ID" : "en-US", {
    year: "numeric", month: "short", day: "numeric",
  }).format(date);
}

export default function PublicUsers() {
  const { lang } = useLang();
  const c = copy[lang] || copy.en;
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [verified, setVerified] = useState("all");
  const [sort, setSort] = useState("newest");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(false);
    const params = { page, page_size: pageSize, status, sort };
    if (search) params.search = search;
    if (verified !== "all") params.verified = verified === "verified";
    api.get("/admin/public-users", { params })
      .then((response) => { if (current) setData(response.data); })
      .catch(() => { if (current) setError(true); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [page, pageSize, search, status, verified, sort]);

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const resetPage = (setter) => (value) => { setter(value); setPage(1); };
  const submitSearch = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  return (
    <AdminLayout>
      <header className="mb-6">
        <h1 className="font-display text-3xl tracking-tight">{c.title}</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{c.subtitle}</p>
      </header>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <form onSubmit={submitSearch} className="flex gap-2 sm:col-span-2 xl:col-span-1">
          <Input aria-label={c.search} data-testid="public-users-search" maxLength={254} placeholder={c.search} value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
          <Button type="submit" size="icon" variant="outline" aria-label={c.search}><Search /></Button>
        </form>
        <DirectorySelect label={c.status} testId="public-users-status" value={status} onChange={resetPage(setStatus)} options={[["all", c.all], ["active", c.active], ["deleting", c.deleting]]} />
        <DirectorySelect label={c.verification} testId="public-users-verification" value={verified} onChange={resetPage(setVerified)} options={[["all", c.all], ["verified", c.verified], ["unverified", c.unverified]]} />
        <DirectorySelect label={c.sort} testId="public-users-sort" value={sort} onChange={resetPage(setSort)} options={[["newest", c.newest], ["oldest", c.oldest], ["email_asc", c.emailAsc], ["email_desc", c.emailDesc]]} />
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-x-auto" data-testid="public-users-scroll-container">
        <table className="w-full min-w-[760px] text-sm" data-testid="public-users-table">
          <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-4 text-left">Email</th><th className="p-4 text-left">{c.status}</th><th className="p-4 text-left">{c.verification}</th><th className="p-4 text-right">{c.saved}</th><th className="p-4 text-left">{c.joined}</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && Array.from({ length: 3 }).map((_, index) => <tr key={index} data-testid="public-users-loading"><td colSpan={5} className="p-4"><Skeleton className="h-6 w-full" /></td></tr>)}
            {!loading && !error && data.items.map((user) => (
              <tr key={user.id} data-testid="public-user-row">
                <td className="p-4 font-medium">{user.email}</td>
                <td className="p-4"><Badge variant={user.status === "active" ? "secondary" : "outline"}>{user.status === "deleting" ? c.deleting : c.active}</Badge></td>
                <td className="p-4">{user.email_verified ? c.verified : c.unverified}</td>
                <td className="p-4 text-right tabular-nums">{user.saved_results_count}</td>
                <td className="p-4 whitespace-nowrap text-muted-foreground">{joinedDate(user.created_at, lang)}</td>
              </tr>
            ))}
            {!loading && error && <tr><td colSpan={5} className="p-8 text-center text-destructive" role="alert">{c.error}</td></tr>}
            {!loading && !error && data.items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{c.empty}</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
        <DirectorySelect label="" testId="public-users-page-size" value={String(pageSize)} onChange={(value) => { setPageSize(Number(value)); setPage(1); }} options={[["25", "25"], ["50", "50"], ["100", "100"]]} />
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>{c.previous}</Button>
          <span>{c.page} {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)}>{c.next}</Button>
        </div>
      </div>
    </AdminLayout>
  );
}

function DirectorySelect({ label, testId, value, onChange, options }) {
  return (
    <div className="min-w-0">
      {label && <span className="sr-only">{label}</span>}
      <select
        aria-label={label || testId}
        data-testid={testId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </div>
  );
}
